import { useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import type { Row } from './admin-collections.config';



/* ==========================================================================
   ATENDIMENTO E MENSAGENS (Tabela contact_messages)
   ========================================================================== */

export type MessageStatus = 'new' | 'read' | 'closed';

/**
 * Interface estrita para as mensagens de contato
 */
export interface ContactMessage extends Row {
  id: string | number;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: MessageStatus;
  created_at?: string;
}

/**
 * Módulo de gerenciamento de mensagens recebidas pelo formulário de contato.
 * Permite listagem em tempo real e atualização de status (nova, lida, fechada).
 */
export function MessagesEditor() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  /**
   * Busca a lista de mensagens na API
   */
  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await api.messages();
      setMessages(data as ContactMessage[]);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar mensagens.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  /**
   * Altera o status da mensagem no banco de dados
   */
  async function updateStatus(id: string | number, status: MessageStatus): Promise<void> {
    try {
      await api.updateMessage(String(id), status);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar status.');
    }
  }

  return (
    <section className="admin-section" aria-labelledby="messages-title">
      <div className="admin-section-head">
        <div>
          <span className="section-tag">ATENDIMENTO</span>
          <h2 id="messages-title">Mensagens recebidas</h2>
        </div>
        <button className="btn btn-outline" onClick={() => void load()} disabled={loading}>
          {loading ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Contato</th>
              <th>Assunto</th>
              <th>Mensagem</th>
              <th>Status</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '24px' }}>
                  Nenhuma mensagem recebida até o momento.
                </td>
              </tr>
            ) : (
              messages.map((message) => (
                <tr key={message.id}>
                  <td>
                    <strong>{message.name}</strong>
                    <small>
                      {message.email}
                      {message.phone ? ` • ${message.phone}` : ''}
                    </small>
                  </td>
                  <td>{message.subject}</td>
                  <td>
                    <small>{message.message}</small>
                  </td>
                  <td>
                    <span className={`status-pill status-${message.status}`}>
                      {message.status === 'new'
                        ? 'Nova'
                        : message.status === 'read'
                          ? 'Lida'
                          : 'Fechada'}
                    </span>
                  </td>
                  <td>
                    <select
                      value={message.status}
                      onChange={(e) =>
                        void updateStatus(message.id, e.target.value as MessageStatus)
                      }
                      aria-label={`Status da mensagem de ${message.name}`}
                    >
                      <option value="new">Nova</option>
                      <option value="read">Lida</option>
                      <option value="closed">Fechada</option>
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}